import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { HiEye, HiEyeOff, HiExclamationCircle, HiArrowLeft } from 'react-icons/hi';

import { loginWithEmail } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AuthLoginEmailPage = () => {
    const navigate = useNavigate();
    const { refreshAuth } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const isEmailValid = EMAIL_REGEX.test(email);
    const showEmailError = emailTouched && email !== '' && !isEmailValid;
    const isActive = isEmailValid && password !== '';

    const handleSubmit = async () => {
        if (!isActive || isLoading) return;
        setIsLoading(true);
        setErrorMessage('');
        try {
            await loginWithEmail({ email, password });
            const userData = await refreshAuth();
            if (userData && (!userData.has_store || !userData.has_channel)) {
                navigate('/onboarding', { replace: true });
            } else {
                navigate('/stores', { replace: true });
            }
        } catch (e) {
            setErrorMessage(e.response?.data?.message ?? '이메일 또는 비밀번호를 확인해주세요.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSubmit();
    };

    return (
        <div style={{ width: '100dvw', height: '100dvh', display: 'flex' }}>
            <BackButton onClick={() => navigate(-1)}>
                <HiArrowLeft size={22} color="#555555" />
            </BackButton>
            <BackgroundLayout>
                <FormWrapper>
                    <Title>이메일로 로그인</Title>

                    <FieldGroup>
                        <Label>이메일</Label>
                        <Input
                            type="email"
                            placeholder="이메일을 입력해주세요"
                            value={email}
                            $error={showEmailError}
                            onChange={(e) => setEmail(e.target.value)}
                            onBlur={() => setEmailTouched(true)}
                            onKeyDown={handleKeyDown}
                        />
                        {showEmailError && (
                            <ErrorMessage>
                                <HiExclamationCircle size={16} color="#EF4444" />
                                올바른 이메일 형식이 아닙니다.
                            </ErrorMessage>
                        )}
                    </FieldGroup>

                    <FieldGroup>
                        <Label>비밀번호</Label>
                        <PasswordWrapper>
                            <Input
                                as="input"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="비밀번호를 입력해주세요"
                                value={password}
                                $error={!!errorMessage}
                                onChange={(e) => { setPassword(e.target.value); setErrorMessage(''); }}
                                onKeyDown={handleKeyDown}
                                style={{ paddingRight: '48px' }}
                            />
                            <EyeButton type="button" onClick={() => setShowPassword((v) => !v)}>
                                {showPassword ? <HiEye size={20} color="#AAAAAA" /> : <HiEyeOff size={20} color="#AAAAAA" />}
                            </EyeButton>
                        </PasswordWrapper>
                        {errorMessage && (
                            <ErrorMessage>
                                <HiExclamationCircle size={16} color="#EF4444" />
                                {errorMessage}
                            </ErrorMessage>
                        )}
                    </FieldGroup>

                    <SubmitButton $active={isActive && !isLoading} onClick={handleSubmit}>
                        {isLoading ? '로그인 중...' : '로그인'}
                    </SubmitButton>

                    <BottomText>
                        아직 회원이 아니신가요?&nbsp;
                        <RegisterLink onClick={() => navigate('/auth/register')}>회원가입</RegisterLink>
                    </BottomText>
                </FormWrapper>
            </BackgroundLayout>
        </div>
    );
};

const BackButton = styled.button`
    position: fixed;
    top: 24px;
    left: 24px;
    background: none;
    border: none;
    padding: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: background 0.15s;
    z-index: 10;

    &:hover { background: #F3F4F6; }
`;

const BackgroundLayout = styled.div`
    flex: 1;
    background: #FFFFFF;
    display: flex;
    align-items: center;
    justify-content: center;

    @media ${({ theme }) => theme.tablet} {
        background: #FFFFFF;
        padding: 40px;
    }

    @media ${({ theme }) => theme.mobile} {
        background: #FFFFFF;
        padding: 20px;
    }
`;

const FormWrapper = styled.div`
    width: 480px;
    padding: 40px;
    // border-radius: 16px;
    // border: 1px solid #E5E8EB;
    // box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
    background: #FFFFFF;
    display: flex;
    flex-direction: column;
    align-items: stretch;

    @media ${({ theme }) => theme.tablet} {
        padding: 0px;
        box-shadow: none;
        border: none;
        width: 100%;
    }
`;

const Title = styled.h1`
    font-family: Pretendard;
    font-size: 24px;
    font-weight: 700;
    color: #232323;
    text-align: center;
    margin: 0 0 32px;
    letter-spacing: -0.5px;
`;

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;
`;

const Label = styled.span`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    color: #232323;
    letter-spacing: -0.3px;
`;

const Input = styled.input`
    height: 56px;
    padding: 0 16px;
    border-radius: 12px;
    border: 1px solid ${({ $error }) => ($error ? '#EF4444' : '#E5E7EB')};
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    color: #232323;
    outline: none;
    box-sizing: border-box;
    width: 100%;
    background: #FFFFFF;
    transition: border-color 0.15s;

    &::placeholder {
        color: #AAAAAA;
    }

    &:focus {
        border-color: ${({ $error }) => ($error ? '#EF4444' : '#3D3D57')};
    }
`;

const PasswordWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const EyeButton = styled.button`
    position: absolute;
    right: 14px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
`;

const ErrorMessage = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 400;
    color: #EF4444;
    letter-spacing: -0.3px;
`;

const SubmitButton = styled.button`
    height: 56px;
    border-radius: 12px;
    border: none;
    background: ${({ $active }) => ($active ? '#3D3D57' : '#D1D5DB')};
    color: #FFFFFF;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.6px;
    cursor: ${({ $active }) => ($active ? 'pointer' : 'default')};
    margin-top: 8px;
    transition: background 0.15s;
`;

const BottomText = styled.p`
    margin: 24px 0 0;
    text-align: center;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    color: #999999;
    letter-spacing: -0.3px;
`;

const RegisterLink = styled.span`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    color: #3D3D57;
    cursor: pointer;
    text-decoration: underline;
`;

export default AuthLoginEmailPage;
